import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiFetch, readJsonResponse } from '../api/client';
import RiskCaseDetailModal from '../components/RiskCaseDetailModal';
import RisksTopBar from '../components/RisksTopBar';
import VirtualList from '../components/VirtualList';
import { useSkeletonVisibility } from '../hooks/useSkeletonVisibility';
import { buildRiskCases } from './buildRiskCases';
import { buildRiskQuery, DEFAULT_FILTERS, FILTER_FETCH_DEBOUNCE_MS } from './dashboardQuery';
import {
  isAllUrgentRisks,
  mergeUrgentItems,
  groupUrgentItems,
  selectedUrgentDeadMonths,
  selectedUrgentRisk,
  selectedUrgentSeverity,
  URGENT_DEAD_DEVICE_MONTH_OPTIONS,
  URGENT_QUADRANTS,
  URGENT_RISK_OPTIONS,
  URGENT_SEVERITY_OPTIONS,
  formatDeadDeviceMonthOption,
  urgentFetchJobs,
  urgentRiskTypeLabel,
} from './urgentQuery';

const TITLE_BY_RISK = {
  all: 'Срочное · все риски',
  1: 'Не на связи — показания не уйдут',
  2: 'Показания не передались',
  3: 'Плохой сигнал',
  4: 'Слабая батарея',
};
const STATUS_RESOLVED = 'resolved';

function accentFromT(t) {
  const hue = Math.round(40 * (1 - t) + 14);
  return {
    bar: `hsl(${hue} 46% 58%)`,
    wash: `hsl(${hue} 42% 97%)`,
    ink: `hsl(${hue} 36% 38%)`,
    pill: `hsl(${hue} 42% 52%)`,
  };
}

function accentScore(score) {
  const value = Math.max(25, Math.min(100, Number(score) || 25));
  return accentFromT((value - 25) / 75);
}

/** Operator accent by risk band — days/slots are not shown on Urgent. */
function accentLevel(level) {
  const key = String(level || '').trim();
  if (key === 'very_high') return accentFromT(1);
  if (key === 'high') return accentFromT(0.66);
  if (key === 'medium') return accentFromT(0.33);
  return accentFromT(0.5);
}

const RESOLVED = {
  bar: '#16a34a',
  wash: '#f0fdf4',
  ink: '#166534',
  pill: '#16a34a',
};

function matrixSummary(groups) {
  const parts = [
    groups.now.length ? `${groups.now.length} сейчас` : '',
    groups.plan.length ? `${groups.plan.length} в план` : '',
    groups.delegate.length ? `${groups.delegate.length} в обход` : '',
  ].filter(Boolean);
  return parts.join(' · ') || 'Нет устройств';
}

function isOfflineCard(item) {
  return Number(item.riskNumber) === 1 || (item.daysLeft != null && item.score == null);
}

function typeLine(item) {
  const typeLabel = item.riskLabel || urgentRiskTypeLabel(item.riskNumber);
  return [typeLabel, item.company].filter(Boolean).join(' · ');
}

const WHY_PREFIX = 'Эта карточка здесь потому что';

/** Backend whyHere = bare clause; Urgent FE adds the prefix. Same row height. */
function formatWhyHere(item) {
  const why = String(item.whyHere || '').trim();
  if (!why) return '';
  if (/^эта карточка здесь потому что/i.test(why)) return why;
  return `${WHY_PREFIX} ${why}`;
}

function reasonLine(item) {
  return formatWhyHere(item) || typeLine(item) || '—';
}

function OpenRiskCardButton({ item, opening, onOpen }) {
  const serial = String(item.serial || '').trim();
  return (
    <button
      type="button"
      aria-label="Карточка риска"
      title="Карточка риска"
      disabled={!serial || opening}
      onClick={() => onOpen?.(item)}
      className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-slate-300 bg-white text-[11px] leading-none text-slate-600 hover:border-sky-400 hover:text-sky-700 disabled:opacity-50"
    >
      ℹ
    </button>
  );
}

function AlertCard({ item, opening, onOpen }) {
  const isResolved = item.status === STATUS_RESOLVED;
  const color = isResolved ? RESOLVED : accentLevel(item.level);
  const label = isResolved ? 'на связи' : item.levelLabel || item.level || '—';
  const meta = [item.phone, item.address].filter(Boolean).join(' · ');
  const serial = String(item.serial || '').trim();
  const decisionTag = item.decision?.quadrant
    ? `${item.decision.quadrant}${item.decision.support_eligible ? '·support' : item.decision.auto_notify_eligible ? '·auto' : ''}`
    : '';

  const reason = reasonLine(item);
  const tip = [formatWhyHere(item), typeLine(item)].filter(Boolean).join('\n');

  return (
    <article
      className="relative overflow-hidden rounded-lg pl-2.5 pr-2.5 py-1.5"
      style={{ backgroundColor: color.wash }}
      title={tip || undefined}
    >
      <span
        aria-hidden="true"
        className="absolute inset-y-0 left-0 w-[3px]"
        style={{ backgroundColor: color.bar }}
      />
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <p className="truncate text-sm text-slate-700 dark:text-slate-800">{item.fio}</p>
            {serial ? (
              <span className="shrink-0 text-[11px] tabular-nums text-slate-400">{serial}</span>
            ) : null}
          </div>
          {meta ? <p className="mt-0.5 truncate text-xs text-slate-500">{meta}</p> : null}
          <div className="mt-0.5 flex items-baseline justify-between gap-2">
            <p className="min-w-0 truncate text-xs" style={{ color: color.ink }}>
              {reason}
            </p>
            {decisionTag ? <span className="shrink-0 text-[11px] text-slate-400">{decisionTag}</span> : null}
          </div>
        </div>
        <span
          className="mt-0.5 shrink-0 rounded-full px-2 py-0.5 text-[11px] leading-4 text-white"
          style={{ backgroundColor: color.pill }}
        >
          {label}
        </span>
        <OpenRiskCardButton item={item} opening={opening} onOpen={onOpen} />
      </div>
    </article>
  );
}

function SignalCard({ item, opening, onOpen }) {
  const color = Number.isFinite(Number(item.score)) ? accentScore(item.score) : accentLevel(item.level);
  const serial = String(item.serial || '').trim();
  const meta = [item.phone, item.address].filter(Boolean).join(' · ');
  const decisionTag = item.decision?.quadrant
    ? `${item.decision.quadrant}${item.decision.support_eligible ? '·support' : item.decision.auto_notify_eligible ? '·auto' : ''}`
    : '';
  const reason = reasonLine(item);
  const tip = [formatWhyHere(item), typeLine(item)].filter(Boolean).join('\n');

  return (
    <article
      className="relative overflow-hidden rounded-lg pl-2.5 pr-2.5 py-1.5"
      style={{ backgroundColor: color.wash }}
      title={tip || undefined}
    >
      <span
        aria-hidden="true"
        className="absolute inset-y-0 left-0 w-[3px]"
        style={{ backgroundColor: color.bar }}
      />
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <p className="truncate text-sm text-slate-700 dark:text-slate-800">{item.fio}</p>
            {serial ? (
              <span className="shrink-0 text-[11px] tabular-nums text-slate-400">{serial}</span>
            ) : null}
          </div>
          {meta ? <p className="mt-0.5 truncate text-xs text-slate-500">{meta}</p> : null}
          <div className="mt-0.5 flex items-baseline justify-between gap-2">
            <p className="min-w-0 truncate text-xs" style={{ color: color.ink }}>
              {reason}
            </p>
            {decisionTag ? <span className="shrink-0 text-[11px] text-slate-400">{decisionTag}</span> : null}
          </div>
        </div>
        <span
          className="mt-0.5 shrink-0 rounded-full px-2 py-0.5 text-[11px] leading-4 text-white"
          style={{ backgroundColor: color.pill }}
        >
          {item.levelLabel}
        </span>
        <OpenRiskCardButton item={item} opening={opening} onOpen={onOpen} />
      </div>
    </article>
  );
}

export default function UrgentPage({ activeTab = 'urgent', onTabChange: _onTabChange } = {}) {
  const panelActive = activeTab === 'urgent';
  const [params, setParams] = useSearchParams();
  const risk = selectedUrgentRisk(params.get('risk'));
  const severity = selectedUrgentSeverity(params.get('severity'));
  const deadMonths = selectedUrgentDeadMonths(params.get('deadDeviceMonths'));
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const showSkeleton = useSkeletonVisibility(isLoading && !data);
  const [detailCase, setDetailCase] = useState(null);
  const [detailRiskNumber, setDetailRiskNumber] = useState(null);
  const [cardError, setCardError] = useState('');
  const [openingKey, setOpeningKey] = useState('');

  const setFilter = (key, next) => {
    const nextParams = new URLSearchParams(params);
    if (key === 'risk') {
      if (next === 'all') {
        nextParams.delete('risk');
      } else {
        nextParams.set('risk', next);
      }
    }
    if (key === 'deadDeviceMonths') {
      const nextMonths = selectedUrgentDeadMonths(next);
      if (nextMonths === selectedUrgentDeadMonths(null)) {
        nextParams.delete('deadDeviceMonths');
      } else {
        nextParams.set('deadDeviceMonths', nextMonths);
      }
    }
    if (key === 'severity') {
      const nextSeverity = selectedUrgentSeverity(next);
      if (nextSeverity === selectedUrgentSeverity(null)) {
        nextParams.delete('severity');
      } else {
        nextParams.set('severity', nextSeverity);
      }
    }
    setParams(nextParams, { replace: true });
  };

  const load = useCallback(async () => {
    setData(null);
    setIsLoading(true);
    setError('');
    const jobs = urgentFetchJobs(risk, severity, deadMonths);
    try {
      const payloads = await Promise.all(
        jobs.map(async (job) => {
          const response = await apiFetch(job.path);
          const payload = await readJsonResponse(response, job.label);
          if (!response.ok) {
            throw new Error(payload.message || `HTTP ${response.status}`);
          }
          return { ...payload, riskNumber: job.riskNumber };
        }),
      );

      if (isAllUrgentRisks(risk)) {
        const items = mergeUrgentItems(payloads);
        setData({
          title: TITLE_BY_RISK.all,
          items,
          open_count: items.length,
          resolved_count: 0,
          count: items.length,
        });
      } else {
        const payload = payloads[0] || {};
        const riskNumber = Number(payload.riskNumber) || Number(risk);
        const items = (payload.items || []).map((item) => ({
          ...item,
          riskNumber: item.riskNumber || riskNumber,
          riskLabel: urgentRiskTypeLabel(item.riskNumber || riskNumber),
        }));
        setData({
          ...payload,
          items,
          title: payload.title || TITLE_BY_RISK[risk],
        });
      }
    } catch (err) {
      setError(err.message || 'Не удалось загрузить');
      setData(null);
      return false;
    } finally {
      setIsLoading(false);
    }
    return true;
  }, [risk, severity, deadMonths]);

  const seenRef = useRef(null);
  const urgentVisibleRef = useRef(false);
  useEffect(() => {
    if (!panelActive) {
      urgentVisibleRef.current = false;
      return undefined;
    }
    const key = `${risk}|${severity}|${deadMonths}`;
    const justShown = !urgentVisibleRef.current;
    urgentVisibleRef.current = true;
    if (justShown && seenRef.current === key) {
      return undefined;
    }
    const first = seenRef.current === null;
    const delayMs =
      typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'test'
        ? 0
        : first
          ? 0
          : FILTER_FETCH_DEBOUNCE_MS;
    const timer = setTimeout(() => {
      load().then((ok) => {
        if (ok) {
          seenRef.current = key;
        }
      });
    }, delayMs);
    return () => clearTimeout(timer);
  }, [panelActive, load, risk, severity, deadMonths]);

  const items = data?.items || [];
  const groups = groupUrgentItems(items);
  const visibleCount = groups.now.length + groups.plan.length + groups.delegate.length;
  const title = data?.title || TITLE_BY_RISK[risk] || TITLE_BY_RISK.all;

  const openRiskCard = useCallback(async (item) => {
    const serial = String(item.serial || '').trim();
    const riskNumber = Number(item.riskNumber) || 1;
    if (!serial) {
      setCardError('Нет серийного номера');
      return;
    }
    setOpeningKey(item.deviceKey || serial);
    setCardError('');
    try {
      const qs = buildRiskQuery({
        ...DEFAULT_FILTERS,
        page: 1,
        pageSize: 20,
        q: serial,
        showAll: true,
        hideTest: false,
      });
      const response = await apiFetch(`/api/risk/${riskNumber}/context?${qs}`);
      const payload = await readJsonResponse(response, 'Risk card');
      if (!response.ok) {
        throw new Error(payload.message || `HTTP ${response.status}`);
      }
      const hits = (payload.items || []).map((hit) => ({
        ...hit,
        risk_number: hit.risk_number ?? riskNumber,
      }));
      const cases = buildRiskCases(hits);
      const needle = serial.toLowerCase();
      const match =
        cases.find((riskCase) => {
          const headerSerial = String(riskCase.header?.controller_serial || '').toLowerCase();
          if (headerSerial.includes(needle)) {
            return true;
          }
          return (riskCase.hits || []).some((hit) => {
            const facts = hit.facts || {};
            return [facts.serial_number, facts.controller_serial_number, facts.counter_serial_number].some(
              (value) => String(value || '').toLowerCase().includes(needle),
            );
          });
        }) || cases[0];
      if (!match) {
        setCardError('Карточка не найдена');
        return;
      }
      setDetailRiskNumber(riskNumber);
      setDetailCase(match);
    } catch (err) {
      setCardError(err.message || 'Не удалось открыть карточку');
    } finally {
      setOpeningKey('');
    }
  }, []);

  return (
    <section className="mx-auto w-full max-w-5xl space-y-3">
      <RisksTopBar
        actions={
          <div className="grid w-full min-w-0 grid-cols-1 gap-2 min-[640px]:grid-cols-[minmax(0,1.2fr)_minmax(8.5rem,11rem)_minmax(7rem,9rem)_auto] min-[640px]:items-end">
            <label className="flex min-w-0 flex-col gap-1">
              <span className="text-[11px] font-medium leading-none text-slate-500">Риск</span>
              <select
                aria-label="Риск"
                value={risk}
                onChange={(event) => setFilter('risk', event.target.value)}
                className="h-9 w-full min-w-0 rounded-lg border border-slate-300 bg-white px-2.5 text-sm text-slate-800 shadow-sm outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-400/20 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              >
                {URGENT_RISK_OPTIONS.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex min-w-0 flex-col gap-1">
              <span className="text-[11px] font-medium leading-none text-slate-500">Уровень</span>
              <select
                aria-label="Уровень"
                value={severity}
                onChange={(event) => setFilter('severity', event.target.value)}
                className="h-9 w-full min-w-0 rounded-lg border border-slate-300 bg-white px-2.5 text-sm text-slate-800 shadow-sm outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-400/20 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              >
                {URGENT_SEVERITY_OPTIONS.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex min-w-0 flex-col gap-1">
              <span className="text-[11px] font-medium leading-none text-slate-500">Мёртвое</span>
              <select
                aria-label="Мёртвое"
                value={deadMonths}
                onChange={(event) => setFilter('deadDeviceMonths', event.target.value)}
                className="h-9 w-full min-w-0 rounded-lg border border-slate-300 bg-white px-2.5 text-sm text-slate-800 shadow-sm outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-400/20 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              >
                {URGENT_DEAD_DEVICE_MONTH_OPTIONS.map((months) => (
                  <option key={months} value={String(months)}>
                    {formatDeadDeviceMonthOption(months)}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              disabled={isLoading}
              onClick={load}
              className="inline-flex h-9 w-full shrink-0 items-center justify-center rounded-lg border border-slate-300 bg-white px-3 text-sm font-medium text-slate-800 shadow-sm transition hover:border-slate-400 hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800 min-[480px]:w-[6.75rem]"
            >
              Обновить
            </button>
          </div>
        }
      />

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950/70">
        <div className="border-b border-slate-200 px-3 py-2.5 dark:border-slate-800">
          <h2 className="text-sm font-medium text-slate-700 dark:text-slate-200">{title}</h2>
          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">Дуайт Эйзенхауэр</p>
          <p className="mt-0.5 text-xs text-slate-400">{matrixSummary(groups)}</p>
          {cardError ? <p className="mt-1 text-xs text-rose-600">{cardError}</p> : null}
        </div>

        {error ? (
          <p className="px-3 py-4 text-sm text-rose-600 dark:text-rose-400">{error}</p>
        ) : null}

        {showSkeleton ? (
          <div className="space-y-1.5 p-3">
            {[0, 1, 2].map((key) => (
              <div
                key={key}
                className="h-14 animate-pulse rounded-lg bg-slate-100 dark:bg-slate-900"
              />
            ))}
          </div>
        ) : null}

        {!showSkeleton && !error && visibleCount === 0 ? (
          <p className="px-3 py-6 text-sm text-slate-500">Сейчас срочных устройств нет</p>
        ) : null}

        {!showSkeleton && visibleCount > 0
          ? URGENT_QUADRANTS.map((quadrant) => {
              const quadrantItems = groups[quadrant.id];
              if (!quadrantItems.length) {
                return null;
              }
              return (
                <div key={quadrant.id} className="border-t border-slate-100 px-3 py-3 dark:border-slate-800">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-medium text-slate-800 dark:text-slate-100">{quadrant.title}</h3>
                    <span
                      className="inline-flex h-5 min-w-[1.25rem] shrink-0 items-center justify-center rounded-full bg-orange-500 px-1 text-xs font-semibold leading-none text-white"
                      aria-label={`${quadrantItems.length}`}
                    >
                      {quadrantItems.length > 99 ? '99+' : quadrantItems.length}
                    </span>
                  </div>
                  <p className="mt-0.5 text-xs text-slate-400">{quadrant.hint}</p>
                  <div className="mt-2">
                    <VirtualList
                      items={quadrantItems}
                      itemHeight={88}
                      height={Math.min(420, Math.max(96, quadrantItems.length * 72))}
                      getKey={(item, index) =>
                        item.deviceKey || item.serial || `${item.company || ''}-${index}`
                      }
                      renderItem={(item) => {
                        const opening = openingKey === (item.deviceKey || item.serial);
                        const card = isOfflineCard(item) ? (
                          <AlertCard item={item} opening={opening} onOpen={openRiskCard} />
                        ) : (
                          <SignalCard item={item} opening={opening} onOpen={openRiskCard} />
                        );
                        return <div className="pr-0.5 pb-1.5">{card}</div>;
                      }}
                    />
                  </div>
                </div>
              );
            })
          : null}
      </div>
      {detailCase ? (
        <RiskCaseDetailModal
          riskCase={detailCase}
          initialRiskNumber={detailRiskNumber}
          onClose={() => setDetailCase(null)}
        />
      ) : null}
    </section>
  );
}
